/**
 * Rend inerte tout ce qui, dans `<body>`, n'est pas le calque donné.
 *
 * Un calque plein écran (l'écran de maintenance) ne suffit pas à couper la
 * page dessous : ce qui est TÉLÉPORTÉ dans `<body>` échappe à l'attribut
 * `inert` posé sur le cadre de l'application. Le popup d'appel, une modale
 * restée ouverte, les conteneurs de bandeaux vivent là, et restaient
 * focalisables au clavier et lus par le lecteur d'écran sous le calque.
 *
 * Plutôt que de poser `inert` composant par composant — et d'oublier le
 * prochain `<Teleport to="body">` —, on isole au niveau de `<body>` : chaque
 * enfant qui ne contient pas le calque reçoit `inert` et `aria-hidden`. Un
 * observateur fait de même pour ce qui s'y ajoute ensuite (un toast, une
 * modale ouverte par un minuteur).
 *
 * L'enfant qui CONTIENT le calque (`#app`) n'est pas touché : c'est à son
 * propriétaire de rendre inertes les frères du calque qui y vivent.
 *
 * Seuls les attributs posés ici sont retirés à la libération : un élément déjà
 * inerte pour une autre raison le reste.
 *
 * @param {Element|null|undefined} calque
 * @returns {() => void} libère ce qui a été rendu inerte
 */
export function isolerCalque(calque) {
  if (!calque || typeof document === 'undefined' || !document.body) return () => {}

  /** @type {Map<Element, {inert: boolean, ariaHidden: boolean}>} */
  const poses = new Map()

  const isoler = (el) => {
    if (!(el instanceof Element) || poses.has(el) || el.contains(calque)) return
    const pose = {
      inert: !el.hasAttribute('inert'),
      ariaHidden: !el.hasAttribute('aria-hidden')
    }
    if (pose.inert) el.setAttribute('inert', '')
    if (pose.ariaHidden) el.setAttribute('aria-hidden', 'true')
    poses.set(el, pose)
  }

  Array.from(document.body.children).forEach(isoler)

  const observateur = typeof MutationObserver === 'undefined'
    ? null
    : new MutationObserver((mutations) => {
      mutations.forEach((m) => m.addedNodes.forEach(isoler))
    })
  observateur?.observe(document.body, { childList: true })

  return () => {
    observateur?.disconnect()
    poses.forEach((pose, el) => {
      if (pose.inert) el.removeAttribute('inert')
      if (pose.ariaHidden) el.removeAttribute('aria-hidden')
    })
    poses.clear()
  }
}
